/**
 * Arcade spawn weighting — difficulty ramps with stack height.
 *
 * See docs/PLAN.md §4.3, §4.10 (Arcade spawn weighting).
 */

import type { RunConfig } from './run-config.js';
import type { CatArchetype } from './types.js';

/** Pick the next queue cat archetype from weighted tables. */
export function pickSpawnArchetype(
  archetypes: readonly CatArchetype[],
  config: RunConfig,
  stackHeightAboveBed: number,
  placedCount: number,
  lastArchetypeId: string | null,
  rng: () => number = Math.random,
): CatArchetype {
  const difficulty = spawnDifficulty(stackHeightAboveBed, placedCount, config);
  const weights = archetypes.map((a) => ({
    archetype: a,
    weight: archetypeSpawnWeight(a, difficulty, config),
  }));

  // Anti-frustration: avoid repeating the same archetype twice in a row early run.
  if (placedCount < config.spawnAntiRepeatUntilPlaced && lastArchetypeId) {
    const last = weights.find((w) => w.archetype.id === lastArchetypeId);
    if (last) last.weight *= 0.15;
  }

  const total = weights.reduce((s, w) => s + w.weight, 0);
  if (total <= 0) return archetypes[0] as CatArchetype;
  let roll = rng() * total;
  for (const entry of weights) {
    roll -= entry.weight;
    if (roll <= 0) return entry.archetype;
  }
  return weights[weights.length - 1]?.archetype ?? (archetypes[0] as CatArchetype);
}

/** 0 early run → 1 tall stack. */
export function spawnDifficulty(
  stackHeightAboveBed: number,
  placedCount: number,
  config: RunConfig,
): number {
  const fromHeight = Math.max(
    0,
    (stackHeightAboveBed - config.spawnWeightHeightStart) / config.spawnWeightHeightRamp,
  );
  const fromCount = Math.max(
    0,
    (placedCount - config.spawnWeightCountStart) / config.spawnWeightCountRamp,
  );
  return Math.min(1, Math.max(fromHeight, fromCount));
}

function archetypeSpawnWeight(
  archetype: CatArchetype,
  difficulty: number,
  config: RunConfig,
): number {
  const bias = config.spawnArchetypeBias[archetype.id];
  if (!bias) return 1;
  return bias.early * (1 - difficulty) + bias.late * difficulty;
}
